import React, { useState, useEffect } from 'react';
import { RefreshCw, FileText, TrendingUp, TrendingDown, DollarSign, Clock, CheckCircle, AlertTriangle, Download, Filter, Search, ArrowUpDown, Zap } from 'lucide-react';

const BankReconciliation = () => {
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [dateRange, setDateRange] = useState('month');
  const [transactionStatus, setTransactionStatus] = useState('all');
  const [selectedBankTransactions, setSelectedBankTransactions] = useState([]);
  const [selectedSystemTransactions, setSelectedSystemTransactions] = useState([]);
  const [showManualMatchModal, setShowManualMatchModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Mock data pour les statistiques
  const stats = {
    reconciledBalance: "€847,230.45",
    unmatchedTransactions: 23,
    discrepancies: 3,
    automationRate: 94,
    lastSync: "Il y a 15 min"
  };

  // Mock data pour les suggestions IA
  const aiSuggestions = [
    {
      id: 1,
      bankTransaction: "Virement - Villa Azure - €12,500.00",
      systemTransaction: "Réservation #VR2024-001 - €12,500.00",
      confidence: 96,
      reason: "Montant et référence identiques"
    },
    {
      id: 2,
      bankTransaction: "Commission Booking.com - €890.50",
      systemTransaction: "Commission partenaire #COM-2024-089 - €890.50",
      confidence: 89,
      reason: "Montant et timing cohérents"
    },
    {
      id: 3,
      bankTransaction: "Prélèvement maintenance - €450.00",
      systemTransaction: "Facture maintenance #MAINT-2024-034 - €450.00",
      confidence: 92,
      reason: "Correspondance parfaite"
    }
  ];

  // Mock data pour les transactions bancaires
  const bankTransactions = [
    {
      id: 'bank_1',
      date: '2024-09-14',
      amount: 15500.00,
      reference: 'VIR-2024-001',
      description: 'Virement Villa Côte d\'Azur',
      status: 'matched',
      tags: ['client', 'villa-azure']
    },
    {
      id: 'bank_2',
      date: '2024-09-13',
      amount: -2450.00,
      reference: 'PREL-2024-089',
      description: 'Commission Airbnb',
      status: 'pending',
      tags: ['commission', 'airbnb']
    },
    {
      id: 'bank_3',
      date: '2024-09-12',
      amount: 8900.00,
      reference: 'VIR-2024-002',
      description: 'Virement Château Provence',
      status: 'matched',
      tags: ['client', 'chateau-provence']
    },
    {
      id: 'bank_4',
      date: '2024-09-11',
      amount: -890.50,
      reference: 'COM-2024-012',
      description: 'Commission Booking.com',
      status: 'pending',
      tags: ['commission', 'booking']
    }
  ];

  // Mock data pour les transactions système
  const systemTransactions = [
    {
      id: 'sys_1',
      date: '2024-09-14',
      amount: 15500.00,
      reference: 'RES-2024-034',
      description: 'Réservation Villa Côte d\'Azur - Sophie Martin',
      status: 'matched',
      tags: ['reservation', 'villa-azure']
    },
    {
      id: 'sys_2',
      date: '2024-09-13',
      amount: -2450.00,
      reference: 'COM-2024-089',
      description: 'Commission partenaire Airbnb',
      status: 'pending',
      tags: ['commission', 'airbnb']
    },
    {
      id: 'sys_3',
      date: '2024-09-12',
      amount: 8900.00,
      reference: 'RES-2024-035',
      description: 'Réservation Château Provence - Jean Dubois',
      status: 'matched',
      tags: ['reservation', 'chateau-provence']
    },
    {
      id: 'sys_4',
      date: '2024-09-11',
      amount: -890.50,
      reference: 'COM-2024-090',
      description: 'Commission Booking.com',
      status: 'pending',
      tags: ['commission', 'booking']
    }
  ];

  // Statistiques financières
  const financialStats = [
    {
      label: "Entrées",
      value: "€89,450",
      change: "+12.5%",
      trend: "up",
      icon: TrendingUp,
      color: "green"
    },
    {
      label: "Sorties",
      value: "€23,890",
      change: "-8.2%",
      trend: "down",
      icon: TrendingDown,
      color: "red"
    },
    {
      label: "Commissions",
      value: "€5,670",
      change: "+15.3%",
      trend: "up",
      icon: DollarSign,
      color: "blue"
    },
    {
      label: "En attente",
      value: "€12,340",
      change: "-5.1%",
      trend: "down",
      icon: Clock,
      color: "yellow"
    }
  ];

  const handleAIReconciliation = () => {
    setIsProcessing(true);
    // Simulation du traitement IA
    setTimeout(() => {
      setIsProcessing(false);
      // Logique de réconciliation automatique
    }, 3000);
  };

  const handleManualMatch = () => {
    if (selectedBankTransactions.length > 0 && selectedSystemTransactions.length > 0) {
      setShowManualMatchModal(true);
    }
  };

  const acceptSuggestion = (suggestionId) => {
    // Logique pour accepter une suggestion
    console.log('Suggestion acceptée:', suggestionId);
  };

  const rejectSuggestion = (suggestionId) => {
    // Logique pour rejeter une suggestion
    console.log('Suggestion rejetée:', suggestionId);
  };

  const acceptAllSuggestions = () => {
    // Logique pour accepter toutes les suggestions
    console.log('Toutes les suggestions acceptées');
  };

  const toggleTransactionSelection = (transactionId, type) => {
    if (type === 'bank') {
      setSelectedBankTransactions(prev => 
        prev.includes(transactionId) 
          ? prev.filter(id => id !== transactionId)
          : [...prev, transactionId]
      );
    } else {
      setSelectedSystemTransactions(prev => 
        prev.includes(transactionId) 
          ? prev.filter(id => id !== transactionId)
          : [...prev, transactionId]
      );
    }
  };

  const TransactionCard = ({ transaction, type, isSelected, onSelect }) => (
    <div 
      className={`transaction-card p-4 border rounded-lg cursor-pointer transition-all ${
        isSelected ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200 hover:border-gray-300'
      } ${transaction.status === 'matched' ? 'opacity-60' : ''}`}
      onClick={() => transaction.status !== 'matched' && onSelect(transaction.id, type)}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">
          {new Date(transaction.date).toLocaleDateString('fr-FR')}
        </span>
        <span className={`text-lg font-semibold ${
          transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
        }`}>
          {transaction.amount > 0 ? '+' : ''}€{Math.abs(transaction.amount).toLocaleString()}
        </span>
      </div>
      
      <p className="text-sm text-gray-800 mb-2">{transaction.description}</p>
      
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">{transaction.reference}</span>
        <div className="flex items-center space-x-1">
          {transaction.status === 'matched' && (
            <CheckCircle size={16}
            className="text-green-500" />
          )}
          {transaction.tags.map((tag, index) => (
            <span key={index} className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  const ManualMatchModal = () => {
    if (!showManualMatchModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Rapprochement Manuel
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                Transactions sélectionnées
              </label>
              <p className="text-sm text-gray-700">
                {selectedBankTransactions.length} transaction(s) bancaire(s) et {selectedSystemTransactions.length} transaction(s) système
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                Notes (optionnel)
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                rows="3"
                placeholder="Ajoutez une note pour ce rapprochement..."
              ></textarea>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setShowManualMatchModal(false)}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              onClick={() => {
                // Logique de rapprochement manuel
                setShowManualMatchModal(false);
                setSelectedBankTransactions([]);
                setSelectedSystemTransactions([]);
              }}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              Confirmer le rapprochement
            </button>
          </div>
        </div>
      </div>
    );
  };

  const LoadingOverlay = () => {
    if (!isProcessing) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 text-center">
          <div className="animate-spin w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Réconciliation en cours...</h3>
          <p className="text-gray-600">L'IA analyse les transactions pour identifier les correspondances</p>
        </div>
      </div>
    );
  };

  return (
    <div className="bank-reconciliation">
      {/* En-tête */}
      <div className="page-header bg-white border-b border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Réconciliation Bancaire</h1>
            <p className="text-gray-600 mt-1">Rapprochez automatiquement vos transactions bancaires et système</p>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <button className="flex items-center space-x-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              <FileText size={18} />
              <span>Manuel</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <RefreshCw size={18} />
              <span>Synchroniser Banques</span>
            </button>
          </div>
        </div>
      </div>

      {/* Barre de statut */}
      <div className="status-bar bg-white border-b border-gray-200 p-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{stats.reconciledBalance}</div>
            <div className="text-sm text-gray-600">Solde Réconcilié</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">{stats.unmatchedTransactions}</div>
            <div className="text-sm text-gray-600">Trans. Non Appariées</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{stats.discrepancies}</div>
            <div className="text-sm text-gray-600">Écarts</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{stats.automationRate}%</div>
            <div className="text-sm text-gray-600">Taux d'Automatisation</div>
          </div>
          <div className="text-center">
            <div className="text-sm font-medium text-gray-800">{stats.lastSync}</div>
            <div className="text-sm text-gray-600">Dernière Synchro</div>
          </div>
        </div>
      </div>

      <div className="flex-1 p-6">
        {/* Panneau de contrôle */}
        <div className="control-panel bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-4">
              <select 
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              >
                <option value="all">Tous les comptes</option>
                <option value="main">Compte Principal</option>
                <option value="business">Compte Professionnel</option>
                <option value="escrow">Compte de Séquestre</option>
              </select>

              <input
                type="date"
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              />

              <select 
                value={transactionStatus}
                onChange={(e) => setTransactionStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              >
                <option value="all">Tous les statuts</option>
                <option value="matched">Appariées</option>
                <option value="pending">En attente</option>
                <option value="discrepancy">Écarts</option>
              </select>
            </div>

            <button
              onClick={handleAIReconciliation}
              className="flex items-center space-x-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <Zap size={18} />
              <span>Lancer Réconciliation IA</span>
            </button>
          </div>
        </div>

        {/* Suggestions IA */}
        {aiSuggestions.length > 0 && (
          <div className="ai-suggestions bg-white rounded-lg border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Suggestions IA</h3>
              <button
                onClick={acceptAllSuggestions}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Accepter tout
              </button>
            </div>

            <div className="space-y-3">
              {aiSuggestions.map((suggestion) => (
                <div key={suggestion.id} className="suggestion-item bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Transaction Bancaire</p>
                          <p className="text-sm text-gray-600">{suggestion.bankTransaction}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Transaction Système</p>
                          <p className="text-sm text-gray-600">{suggestion.systemTransaction}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">Confiance:</span>
                          <span className="text-sm font-medium text-green-600">{suggestion.confidence}%</span>
                        </div>
                        <p className="text-sm text-gray-600">{suggestion.reason}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => acceptSuggestion(suggestion.id)}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
                      >
                        Accepter
                      </button>
                      <button
                        onClick={() => rejectSuggestion(suggestion.id)}
                        className="px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                      >
                        Rejeter
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Alerte d'écarts */}
        <div className="discrepancies-alert bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="text-yellow-600" size={20} />
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-800">
                3 écarts détectés nécessitent une révision manuelle
              </p>
              <p className="text-sm text-yellow-700">
                Total des écarts: €1,245.50
              </p>
            </div>
            <button className="px-4 py-2 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700">
              Examiner
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="stats-row mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {financialStats.map((stat, index) => (
              <div key={index} className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <stat.icon className={`text-${stat.color}-600`} size={24} />
                  <span className={`text-sm font-medium ${
                    stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {stat.change}
                  </span>
                </div>
                <div className="text-2xl font-bold text-gray-800">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Zone de rapprochement */}
        <div className="reconciliation-grid">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Transactions bancaires */}
            <div>
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="border-b border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-800">
                      Transactions Bancaires
                    </h3>
                    <span className="text-2xl font-bold text-gray-800">
                      €{bankTransactions.reduce((sum, t) => sum + t.amount, 0).toLocaleString()}
                    </span>
                  </div>
                </div>
                
                <div className="p-4 space-y-3">
                  {bankTransactions.map((transaction) => (
                    <TransactionCard
                      key={transaction.id}
                      transaction={transaction}
                      type="bank"
                      isSelected={selectedBankTransactions.includes(transaction.id)}
                      onSelect={toggleTransactionSelection}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Transactions système */}
            <div>
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="border-b border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-800">
                      Transactions Système
                    </h3>
                    <span className="text-2xl font-bold text-gray-800">
                      €{systemTransactions.reduce((sum, t) => sum + t.amount, 0).toLocaleString()}
                    </span>
                  </div>
                </div>
                
                <div className="p-4 space-y-3">
                  {systemTransactions.map((transaction) => (
                    <TransactionCard
                      key={transaction.id}
                      transaction={transaction}
                      type="system"
                      isSelected={selectedSystemTransactions.includes(transaction.id)}
                      onSelect={toggleTransactionSelection}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Zone de matching */}
          {(selectedBankTransactions.length > 0 || selectedSystemTransactions.length > 0) && (
            <div className="matching-zone mt-6 bg-yellow-50 border-2 border-dashed border-yellow-300 rounded-lg p-6 text-center">
              <ArrowUpDown className="mx-auto text-yellow-600 mb-2" size={24} />
              <p className="text-gray-700 mb-4">
                {selectedBankTransactions.length} transaction(s) bancaire(s) et {selectedSystemTransactions.length} transaction(s) système sélectionnée(s)
              </p>
              <button
                onClick={handleManualMatch}
                disabled={selectedBankTransactions.length === 0 || selectedSystemTransactions.length === 0}
                className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Matcher les Transactions
              </button>
            </div>
          )}
        </div>
      </div>

      <ManualMatchModal />
      <LoadingOverlay />
    </div>
  );
};

export default BankReconciliation;